import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * A bordered, collapsed-by-default section for diff noise — value-only drift,
 * unchanged properties, oversized remainders — so it never drowns out the
 * real changes, while staying one click away.
 */
export function CollapsedSection({
  title,
  hint,
  defaultOpen = false,
  children
}: {
  title: string
  /** A muted parenthetical after the title, explaining what's inside. */
  hint?: string
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = React.useState(defaultOpen)
  return (
    <div className="overflow-hidden rounded-md border border-line">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="group flex w-full cursor-pointer items-center gap-2 bg-bg-sunk/60 px-2 py-1.5 text-left text-11 text-ink-2 transition-colors hover:bg-bg-sunk focus:outline-none focus-visible:ring-2 focus-visible:ring-st-info"
      >
        <span
          className={cn(
            'inline-block text-ink-3 transition-transform duration-200 ease-out group-hover:text-ink-2',
            open && 'rotate-90'
          )}
          aria-hidden="true"
        >
          ▸
        </span>
        <span className="font-semibold tracking-wider uppercase group-hover:text-ink-1 text-ink-2">
          {title}
        </span>
        {hint ? <span className="text-10 text-ink-3">({hint})</span> : null}
        <span className="ml-auto text-10 text-ink-3 group-hover:text-ink-2">
          {open ? 'Hide' : 'Show'}
        </span>
      </button>
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  )
}
